import { prop } from 'ramda'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { RootState } from 'app/store'
import { determineCurrentStack } from 'core/containers/AuthController'
import { getSections } from 'core/plugins/helpers'
import { PluginOptions } from 'core/plugins/model'
import pluginManager from 'core/plugins/pluginManager'
import { matchLinkToPath } from 'core/plugins/route-helpers'
import { permissionsKey } from 'core/session/permissionsReducers'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { isOpenCloud } from 'core/utils/helpers'

const usePluginRouter = (currentPluginId = '') => {
  const { prefs, updatePrefs } = useScopedPreferences()
  const { lastStack } = prefs
  const plugins = pluginManager.getPlugins()
  const { location } = useReactRouter()
  const { pathname, hash } = location
  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const permissions = useSelector(prop(permissionsKey))
  const {
    userDetails: { role },
    features,
  } = session
  const pluginId = currentPluginId || determineCurrentStack(location, lastStack, isOpenCloud())

  const currentPath = `${pathname}${hash}`
  const sections = getSections(plugins, role, features, permissions)

  const currentSection = useMemo(
    () => sections.find((section) => pluginId === section.id),
    [pluginId, sections],
  )
  const currentLink = useMemo(
    () => currentSection.links.find(matchLinkToPath(currentPath)),
    [currentSection, currentPath],
  )

  const currentOptions: PluginOptions = useMemo(() => {
    return plugins?.[pluginId]?.data?.options
  }, [pluginId, plugins])

  return {
    plugins,
    currentPluginId: pluginId,
    sections,
    currentSection,
    currentLink,
    currentPath,
    location,
    currentOptions,
  }
}

export default usePluginRouter
